import { ButtonBase, Grid, IconButton, Link, Typography } from "@mui/material";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { SvgIconTypeMap } from "@mui/material/SvgIcon/SvgIcon";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import { observer } from "mobx-react";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { BeatLoader } from "react-spinners";
import {
    NotificationArgument,
    NotificationType,
} from "../../../stores/RealTimeMessages/MessageStoreBase";
import hexToRGB from "../../../utils/hexToRGB";
import { defaultTimeFromNowFormatter } from "../../ManagerInteractions/Util";
import AcxChipList from "components/UI/AcxChipList";
import theme from "../../../Theme/AppTheme";

interface OwnProps {
    notification: NotificationArgument;
    onClick?: (arg: NotificationArgument) => void;
    onDismiss?: (arg: NotificationArgument) => void;
}

type Props = OwnProps;

const NotificationItem: React.FC<Props> = observer((props) => {
    function notificationDismissCallback() {
        props.onDismiss?.(props.notification);
    }

    //Legacy types that we still want to support Here
    //Error, Success, Info
    //Values that use Override and need different css
    //Comment, Reaction
    if (props.notification.notificationOverride) {
        return (
            <Grid>
                <Grid
                    item
                    container
                    position="absolute"
                    right={"0px"}
                    textAlign={"center"}
                    width={"32px"}
                >
                    <IconButton onClick={notificationDismissCallback}>
                        <CloseIcon
                            sx={{
                                color: hexToRGB(theme.palette.gray.main, 0.6),
                            }}
                            fontSize={"small"}
                        ></CloseIcon>
                    </IconButton>
                </Grid>
                {props.notification.notificationOverride}
            </Grid>
        );
    } else {
        switch (props.notification.type) {
            case NotificationType.Error:
                return (
                    <div
                        style={{
                            paddingLeft: theme.spacing(1),
                            paddingRight: theme.spacing(1),
                        }}
                    >
                        {renderContent(ErrorIcon)}
                    </div>
                );
            case NotificationType.Success:
                return (
                    <div
                        style={{
                            paddingLeft: theme.spacing(1),
                            paddingRight: theme.spacing(1),
                        }}
                    >
                        {renderContent(CheckCircleIcon)}
                    </div>
                );

            default:
            case NotificationType.Info:
                return (
                    <div
                        style={{
                            paddingLeft: theme.spacing(1),
                            paddingRight: theme.spacing(1),
                        }}
                    >
                        {renderContent(InfoIcon)}
                    </div>
                );
        }
    }

    function renderContent(TypeIcon: OverridableComponent<SvgIconTypeMap>) {
        const addtlNotifDetails = props.notification.addtlDetails ?? {};
        const formattedNotificationDetails = Object.entries(
            addtlNotifDetails,
        ).map(([key, value]) => {
            if (key === "classifierNames") {
                return (
                    <Grid key={"rclsfy-chips"} item sx={{ marginLeft: "-2px" }}>
                        <AcxChipList
                            size={"small"}
                            color={"blue"}
                            values={
                                props.notification.addtlDetails?.classifierNames
                            }
                            multiToolTip
                        />
                    </Grid>
                );
            } else if (key === "tenantName") {
                return (
                    <Typography key={"rclsfy-org"}>
                        {"Organization: " + value}
                        <br />
                    </Typography>
                );
            } else if (key === "dateReference") {
                const dateRefAndRange = `${value} Range: ${addtlNotifDetails["startDate"]} - ${addtlNotifDetails["endDate"]}`;
                return (
                    <Typography key={"rclsfy-dateRef"}>
                        {dateRefAndRange}
                    </Typography>
                );
            } else {
                return null;
            }
        });

        return (
            <Grid container>
                <Grid
                    container
                    item
                    xs={12}
                    wrap={"nowrap"}
                    alignItems={"center"}
                    key={"notification-item-" + props.notification.id}
                >
                    {props.notification.type ===
                        NotificationType.InProgress && (
                        <Grid item sx={{ marginRight: theme.spacing(0.5) }}>
                            <BeatLoader
                                size={6}
                                loading={
                                    props.notification.type ===
                                    NotificationType.InProgress
                                }
                            />
                        </Grid>
                    )}

                    <Grid item sx={{ paddingTop: theme.spacing(0.75) }}>
                        <TypeIcon
                            sx={{
                                color:
                                    props.notification.type ===
                                    NotificationType.Success
                                        ? theme.palette.success.main
                                        : props.notification.type ===
                                          NotificationType.Error
                                        ? theme.palette.red.main
                                        : theme.palette.primary.main,
                                backgroundColor: theme.palette.white.main,
                                marginTop: "6px",
                            }}
                            fontSize={"small"}
                        />
                    </Grid>
                    <Grid
                        item
                        xs
                        zeroMinWidth
                        sx={{
                            paddingLeft: "8px",
                            paddingTop: theme.spacing(0.75),
                        }}
                    >
                        <Typography noWrap>
                            <Link className="pendo-ignore"
                                variant="body1"
                                sx={{
                                    color: theme.palette.primary.main,
                                    fontWeight: "bold",
                                    textAlign: "left",
                                    textDecoration: "none",
                                }}
                                component={
                                    props.notification.appPath
                                        ? RouterLink
                                        : "button"
                                }
                                to={`${props.notification.appPath}`}
                                color="textPrimary"
                                style={{
                                    textAlign: "left",
                                    pointerEvents: !props.notification.appPath
                                        ? "none"
                                        : "auto",
                                }}
                            >
                                {props.notification.title}
                            </Link>
                        </Typography>
                        {props.notification.subTitle && (
                            <Typography
                                noWrap
                                sx={{
                                    fontSize: "12px",
                                    color: theme.palette.text.secondary,
                                    paddingBottom: theme.spacing(0.5),
                                }}
                            >
                                {props.notification.subTitle}
                            </Typography>
                        )}
                    </Grid>
                    <Grid
                        item
                        xs={1}
                        container
                        justifyContent={"flex-end"}
                        alignItems={"flex-start"}
                    >
                        <ButtonBase onClick={notificationDismissCallback}>
                            <CloseIcon
                                sx={{
                                    color: hexToRGB(
                                        theme.palette.gray.main,
                                        0.6,
                                    ),
                                }}
                                fontSize={"small"}
                            ></CloseIcon>
                        </ButtonBase>
                    </Grid>
                </Grid>
                <Grid
                    item
                    xs={12}
                    sx={{
                        paddingLeft: theme.spacing(0.75),
                        paddingTop: theme.spacing(0.5),
                    }}
                    zeroMinWidth
                >
                    {Boolean(typeof props.notification.message === "string") ? (
                        <>
                            <Typography
                                display={"block"}
                                noWrap
                                sx={{
                                    fontSize: "13px",
                                    overflowWrap: "break-word",
                                    whiteSpace: "pre-wrap",
                                    borderRadius: 1,
                                }}
                            >
                                {props.notification.message}
                                <br />
                            </Typography>
                            {formattedNotificationDetails}
                        </>
                    ) : (
                        <>{props.notification.message}</>
                    )}
                </Grid>

                <Grid
                    item
                    xs={12}
                    container
                    justifyContent={"flex-end"}
                    sx={{ marginTop: theme.spacing(1.5) }}
                >
                    <Typography
                        sx={{
                            fontSize: "13px",
                            overflowWrap: "break-word",
                            whiteSpace: "pre-wrap",
                        }}
                    >
                        {defaultTimeFromNowFormatter(
                            props.notification.timestamp,
                        )}
                    </Typography>
                </Grid>
            </Grid>
        );
    }
});

export default NotificationItem;
